import { useMemo } from 'react';
import { useAuth } from '@frontend/contexts/AuthContext';
import { useBillingAccess } from './useBillingAccess';

export interface ProjectCapabilityResult {
  allowed: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export interface ProjectCapabilityOptions {
  adminBypass?: boolean;
}

export function useProjectCapability(
  projectId: string | undefined,
  capability: string,
  options: ProjectCapabilityOptions = {},
): ProjectCapabilityResult {
  const { isAdmin, adminLoaded } = useAuth() as {
    isAdmin: boolean;
    adminLoaded: boolean;
  };
  const adminBypass = options.adminBypass ?? true;
  const billing = useBillingAccess(projectId, { disabled: !adminLoaded || (isAdmin && adminBypass) });

  return useMemo(() => {
    if (!adminLoaded) {
      return {
        allowed: false,
        loading: true,
        error: null,
        refresh: billing.refresh,
      };
    }
    if (isAdmin && adminBypass) {
      return {
        allowed: true,
        loading: false,
        error: null,
        refresh: billing.refresh,
      };
    }
    const waitingForAccess = Boolean(projectId) && !billing.access && !billing.error;
    return {
      allowed: billing.can(capability),
      loading: billing.loading || waitingForAccess,
      error: billing.error,
      refresh: billing.refresh,
    };
  }, [adminBypass, adminLoaded, billing, capability, isAdmin, projectId]);
}
